import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton, Modal, Tooltip, Chip, useMediaQuery, useTheme } from "@mui/material";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { RootState, useAppDispatch } from "../store";
import { ActivePlanOfCare, AvailableDate, EHR, OmniPatient, Organization, ProviderGroup } from "../types";
import { isDateRange, filterPastAvailableDates } from "../utils/availableDates";
import { useSelector, shallowEqual } from "react-redux";
import { fetchInsurances } from "../slices/InsurancesSlice";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import IosShareIcon from "@mui/icons-material/IosShare";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { DateTime } from "luxon";
import { Parser } from "@json2csv/plainjs";
import { Colors } from "../Colors";
import Select from "../components/select/Select";
import DateRangePicker from "../components/date-range-picker/DateRangePicker";
import PaginatedList from "../components/paginated-list/PaginatedList";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import { api, convertE164ToReadable } from "../utils/utils";
import { usePostHog } from "posthog-js/react";
import { fetchPatientPlansBrief, patientPlansBriefQueryKey } from "../hooks/usePatientPlansBrief";

type ViewWaitlistModalProps = {
  open: boolean;
  onClose: () => void;
};

const surfaces = {
  inactive: "rgba(255, 255, 255, 0.05)",
  inactiveHover: "rgba(255, 255, 255, 0.08)",
  textPrimary: "rgba(255, 255, 255, 0.9)",
  textSubtle: "rgba(255, 255, 255, 0.5)",
  transition: "all 0.15s ease",
  cardRadius: "12px",
  chipRadius: "8px",
  controlRadius: "6px",
};

const getInitials = (first: string, last: string): string => {
  return `${(first || "").charAt(0)}${(last || "").charAt(0)}`.toUpperCase() || "?";
};

const toDateTime = (date: AvailableDate): DateTime => {
  if (typeof date === "string") return DateTime.fromISO(date, { setZone: true });
  if (isDateRange(date)) return DateTime.fromISO(date.startDate, { setZone: true });
  return DateTime.fromISO(date.start, { setZone: true });
};

const formatPlanFrequency = (plan: ActivePlanOfCare): string | null => {
  if (plan.frequency) {
    return `${plan.frequency}x/week`;
  }
  if (plan.gap) {
    return `Every ${plan.gap}d`;
  }
  return null;
};

type EffectiveProviders = { providerIds: string[]; groupIds: string[] };

// A provider assignment (on a plan of care, or on the patient's legacy record) is either a
// specific list of providers or a single provider group — never both.
const getAssignedProviders = (source: { webptProviderIds?: string[]; providerGroupId?: string } | undefined): EffectiveProviders => {
  if (source?.webptProviderIds && source.webptProviderIds.length > 0) {
    return { providerIds: source.webptProviderIds, groupIds: [] };
  }
  if (source?.providerGroupId) {
    return { providerIds: [], groupIds: [source.providerGroupId] };
  }
  return { providerIds: [], groupIds: [] };
};

// Active plans of care can pin their own providers (individually or via a provider group), which
// override the patient's legacy providers. When multiple active plans specify providers, the
// effective set is their union.
const getEffectiveProviders = (patient: OmniPatient, plansOfCare?: ActivePlanOfCare[]): EffectiveProviders => {
  const providerIds = new Set<string>();
  const groupIds = new Set<string>();
  (plansOfCare ?? [])
    .filter((p) => !p.discharged)
    .forEach((plan) => {
      const assigned = getAssignedProviders(plan);
      assigned.providerIds.forEach((id) => providerIds.add(id));
      assigned.groupIds.forEach((id) => groupIds.add(id));
    });
  if (providerIds.size > 0 || groupIds.size > 0) {
    return { providerIds: Array.from(providerIds), groupIds: Array.from(groupIds) };
  }
  return getAssignedProviders(patient?.[EHR.webpt]);
};

const PatientCard: React.FC<{
  patient: OmniPatient;
  providerMap: Map<string, string>;
  providerGroupMap: Map<string, string>;
  patientGroupName?: string;
  insuranceName?: string;
  plansOfCare?: ActivePlanOfCare[];
}> = ({ patient, providerMap, providerGroupMap, patientGroupName, insuranceName, plansOfCare }) => {
  const effectiveProviders = useMemo(() => getEffectiveProviders(patient, plansOfCare), [patient, plansOfCare]);
  const providerNames = effectiveProviders.providerIds.map((id) => providerMap.get(id)).filter(Boolean) as string[];
  const providerGroupNames = effectiveProviders.groupIds.map((id) => providerGroupMap.get(id)).filter(Boolean) as string[];

  const dates = useMemo(() => {
    return filterPastAvailableDates(patient.waitlist.availableDates)
      .filter((d) => toDateTime(d).isValid)
      .sort((a, b) => toDateTime(a).toMillis() - toDateTime(b).toMillis());
  }, [patient.waitlist.availableDates]);

  const visibleDates = dates.slice(0, 6);
  const extraDateCount = Math.max(0, dates.length - visibleDates.length);

  const activePlans = useMemo(() => (plansOfCare ?? []).filter((p) => !p.discharged), [plansOfCare]);
  const visiblePlans = activePlans.slice(0, 2);
  const extraPlanCount = Math.max(0, activePlans.length - visiblePlans.length);

  const initials = getInitials(patient.patient.firstName, patient.patient.lastName);
  const displayName = patient.patient.preferredName
    ? `${patient.patient.firstName} "${patient.patient.preferredName}" ${patient.patient.lastName}`
    : `${patient.patient.firstName} ${patient.patient.lastName}`;
  const readablePhone = patient.patient.phoneNumber ? convertE164ToReadable(patient.patient.phoneNumber) : "";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(200px, 1.3fr) minmax(130px, 0.8fr) minmax(150px, 0.9fr) minmax(150px, 1fr) minmax(220px, 1.5fr)",
        },
        gap: { xs: 1.5, md: 2 },
        alignItems: { xs: "flex-start", md: "center" },
        padding: { xs: "14px", md: "16px 18px" },
        borderRadius: surfaces.cardRadius,
        background: surfaces.inactive,
        border: `1px solid ${Colors.border}`,
        marginBottom: "10px",
        transition: surfaces.transition,
        "&:hover": {
          background: surfaces.inactiveHover,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: surfaces.chipRadius,
            background: Colors.primary,
            color: "#FFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: "13px",
            flexShrink: 0,
          }}
        >
          {initials}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: surfaces.textPrimary,
              fontSize: "0.95rem",
              fontWeight: 600,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25, color: Colors.grey1 }}>
            {readablePhone && <PhoneOutlinedIcon sx={{ fontSize: "0.85rem" }} />}
            <Typography sx={{ fontSize: "13px", color: Colors.grey1 }}>{readablePhone || "No phone on file"}</Typography>
          </Box>
          {patientGroupName && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                padding: "2px 8px",
                borderRadius: surfaces.controlRadius,
                background: surfaces.inactive,
                border: `1px solid ${Colors.border}`,
                marginTop: "6px",
              }}
            >
              <GroupsOutlinedIcon sx={{ fontSize: "0.75rem", color: Colors.primary }} />
              <Typography sx={{ fontSize: "11px", color: Colors.primary, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{patientGroupName}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
        {insuranceName ? (
          <Tooltip title={insuranceName} arrow>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                padding: "4px 10px",
                borderRadius: surfaces.controlRadius,
                background: surfaces.inactive,
                border: `1px solid ${Colors.border}`,
                maxWidth: "100%",
              }}
            >
              <CreditCardOutlinedIcon sx={{ fontSize: "0.8rem", color: surfaces.textPrimary, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontSize: "13px",
                  color: surfaces.textPrimary,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {insuranceName}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography sx={{ fontSize: "13px", color: Colors.grey1, fontStyle: "italic" }}>No insurance</Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
        {plansOfCare === undefined ? (
          <Typography sx={{ fontSize: "13px", color: Colors.grey1, fontStyle: "italic" }}>Loading…</Typography>
        ) : activePlans.length === 0 ? (
          <Typography sx={{ fontSize: "13px", color: Colors.grey1, fontStyle: "italic" }}>No active plan</Typography>
        ) : (
          <>
            {visiblePlans.map((plan) => {
              const label = plan.caseStudyName || plan.name;
              const freq = formatPlanFrequency(plan);
              return (
                <Box
                  key={plan.activePlanOfCareId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    padding: "4px 10px",
                    borderRadius: surfaces.controlRadius,
                    background: surfaces.inactive,
                    border: `1px solid ${Colors.border}`,
                    minWidth: 0,
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ fontSize: "0.8rem", color: surfaces.textPrimary, flexShrink: 0 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: surfaces.textPrimary,
                        fontWeight: 600,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </Typography>
                    {freq && (
                      <Typography
                        sx={{
                          fontSize: "11px",
                          color: Colors.grey1,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          lineHeight: 1.3,
                        }}
                      >
                        {freq}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            {extraPlanCount > 0 && (
              <Tooltip
                title={activePlans
                  .slice(2)
                  .map((p) => {
                    const freq = formatPlanFrequency(p);
                    const name = p.caseStudyName || p.name;
                    return freq ? `${name} (${freq})` : name;
                  })
                  .join(" · ")}
                arrow
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignSelf: "flex-start",
                    alignItems: "center",
                    padding: "2px 10px",
                    borderRadius: surfaces.chipRadius,
                    background: Colors.primary,
                    border: `1px solid ${Colors.primary}`,
                  }}
                >
                  <Typography sx={{ fontSize: "12px", color: "#FFF", fontWeight: 600 }}>+{extraPlanCount} more</Typography>
                </Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
        {providerNames.length > 0 || providerGroupNames.length > 0 ? (
          <>
            {providerGroupNames.map((name, idx) => (
              <Box
                key={`group-${name}-${idx}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  padding: "4px 10px",
                  borderRadius: surfaces.controlRadius,
                  background: surfaces.inactive,
                  border: `1px solid ${Colors.border}`,
                  maxWidth: "100%",
                }}
              >
                <GroupsOutlinedIcon sx={{ fontSize: "0.8rem", color: surfaces.textPrimary }} />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: surfaces.textPrimary,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </Typography>
              </Box>
            ))}
            {providerNames.map((name, idx) => (
              <Box
                key={`${name}-${idx}`}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  padding: "4px 10px",
                  borderRadius: surfaces.controlRadius,
                  background: surfaces.inactive,
                  border: `1px solid ${Colors.border}`,
                  maxWidth: "100%",
                }}
              >
                <BadgeIcon sx={{ fontSize: "0.8rem", color: surfaces.textPrimary }} />
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: surfaces.textPrimary,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </Typography>
              </Box>
            ))}
          </>
        ) : (
          <Typography sx={{ fontSize: "13px", color: Colors.grey1, fontStyle: "italic" }}>No provider assigned</Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
        {visibleDates.length === 0 && <Typography sx={{ fontSize: "13px", color: Colors.grey1, fontStyle: "italic" }}>No available dates</Typography>}
        {visibleDates.map((d) => {
          const start = toDateTime(d);
          const isRange = isDateRange(d);
          const end = isRange ? DateTime.fromISO(d.endDate, { setZone: true }) : null;
          const key = isRange ? d.startDate : start.toISO()!;
          const tooltip = isRange
            ? `${start.toFormat("EEEE, MMMM d, yyyy")} – ${end!.toFormat("EEEE, MMMM d, yyyy")}`
            : start.toFormat("EEEE, MMMM d, yyyy");
          return (
            <Tooltip key={key} title={tooltip} arrow>
              <Box
                sx={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "4px 8px",
                  minWidth: 42,
                  borderRadius: surfaces.chipRadius,
                  background: surfaces.inactive,
                  border: `1px solid ${Colors.border}`,
                  transition: surfaces.transition,
                  "&:hover": { background: surfaces.inactiveHover },
                }}
              >
                {isRange ? (
                  <Typography sx={{ fontSize: "13px", color: surfaces.textPrimary, fontWeight: 600, lineHeight: 1.2 }}>
                    {start.toFormat("MMM d")} – {end!.toFormat("MMM d")}
                  </Typography>
                ) : (
                  <>
                    <Typography sx={{ fontSize: "11px", color: Colors.grey1, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1, fontWeight: 600 }}>{start.toFormat("EEE")}</Typography>
                    <Typography sx={{ fontSize: "13px", color: surfaces.textPrimary, fontWeight: 600, lineHeight: 1.2 }}>{start.toFormat("MMM d")}</Typography>
                  </>
                )}
              </Box>
            </Tooltip>
          );
        })}
        {extraDateCount > 0 && (
          <Tooltip
            title={dates
              .slice(6)
              .map((d) => {
                if (isDateRange(d)) {
                  const s = DateTime.fromISO(d.startDate, { setZone: true });
                  const e = DateTime.fromISO(d.endDate, { setZone: true });
                  return `${s.toFormat("MMM d")} – ${e.toFormat("MMM d")}`;
                }
                return toDateTime(d).toFormat("EEE, MMM d");
              })
              .join(" · ")}
            arrow
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                padding: "0 10px",
                minHeight: 34,
                borderRadius: surfaces.chipRadius,
                background: Colors.primary,
                border: `1px solid ${Colors.primary}`,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#FFF", fontWeight: 600 }}>+{extraDateCount} more</Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const ViewWaitlistModal: React.FC<ViewWaitlistModalProps> = ({ open, onClose }) => {
  const organization: Organization | null = useSelector((state: RootState) => state.auth.organization);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const posthog = usePostHog();

  const getIntegrationVariables = (state: RootState) => {
    const integrations = state.auth.organization?.integrations || [];
    const integration = Object.values(EHR).find((ehr) => integrations.includes(ehr)) as EHR | undefined;
    switch (integration) {
      case EHR.webpt:
        return { providers: state.webptIntegration.providers };
      default:
        return { providers: [] as any[] };
    }
  };

  const integrationVariables = useSelector(getIntegrationVariables, shallowEqual);
  const providerGroups: ProviderGroup[] = useSelector((state: RootState) => state.providers.providerGroups);
  const patients: OmniPatient[] = useSelector((state: RootState) => state.patients.patients);
  const loadingPatients: boolean = useSelector((state: RootState) => state.patients.loadingFetchPatients);
  const insurances = useSelector((state: RootState) => state.insurances.insurances);
  const userToken = useSelector((state: RootState) => state.auth.user?.token);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open && userToken && insurances.length === 0) {
      dispatch(fetchInsurances(userToken));
    }
  }, [open, userToken, insurances.length, dispatch]);

  const insuranceMap = useMemo(() => new Map(insurances.map((i) => [i.insuranceId, i.name])), [insurances]);

  const providers: { id: string; name: string }[] = useMemo(
    () =>
      integrationVariables.providers.map((provider: any) => ({
        id: provider.id,
        name: provider.name || `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim(),
      })),
    [integrationVariables.providers],
  );

  const providerMap = useMemo(() => new Map(providers.map((p) => [p.id, p.name])), [providers]);

  const providerGroupMap = useMemo(() => new Map(providerGroups.map((g) => [g.providerGroupId, g.name])), [providerGroups]);

  const providerGroupMembersMap = useMemo(() => new Map(providerGroups.map((g) => [g.providerGroupId, g.providerIds])), [providerGroups]);

  // Expands a patient's effective provider assignment to individual provider ids, for filtering/search
  // purposes, so a provider-group assignment still matches a search for one of its member providers.
  const expandToProviderIds = (effective: EffectiveProviders): string[] => [
    ...effective.providerIds,
    ...effective.groupIds.flatMap((id) => providerGroupMembersMap.get(id) ?? []),
  ];

  const providerOptions = useMemo(
    () =>
      providers.map((p) => ({
        label: p.name,
        value: p.id,
      })),
    [providers],
  );

  const patientGroupMap = useMemo(() => {
    const map = new Map<string, string>();
    (organization?.patientGroups ?? []).forEach((g) => map.set(g.patientGroupId, g.name));
    return map;
  }, [organization?.patientGroups]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[DateTime | null, DateTime | null]>([null, null]);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setSelectedProviderIds([]);
      setDateRange([null, null]);
    }
  }, [open]);

  const queryClient = useQueryClient();

  const eligiblePatients = useMemo(
    () => patients.filter((p) => p.waitlist.availableDates.length > 0),
    [patients],
  );

  const planQueries = useQueries({
    queries: eligiblePatients.map((p) => ({
      queryKey: patientPlansBriefQueryKey(p.patientId),
      queryFn: () => fetchPatientPlansBrief(p.patientId, userToken!),
      enabled: open && Boolean(userToken),
      staleTime: 0,
      refetchOnMount: "always" as const,
      retry: 1,
    })),
  });

  const plansOfCareMap = useMemo(() => {
    const map: Record<string, ActivePlanOfCare[]> = {};
    eligiblePatients.forEach((p, idx) => {
      const q = planQueries[idx];
      if (q?.data !== undefined) {
        map[p.patientId] = q.data;
      } else if (q?.isError) {
        map[p.patientId] = [];
      }
    });
    return map;
  }, [eligiblePatients, planQueries]);

  const filtersApplied = searchQuery.trim().length > 0 || selectedProviderIds.length > 0 || (dateRange[0] !== null && dateRange[1] !== null);

  const filteredPatients = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const providerFilterSet = new Set(selectedProviderIds);

    let result = patients.filter((patient) => patient.waitlist.availableDates.length > 0);

    if (normalizedQuery) {
      result = result.filter((p) => {
        const phoneDigits = (p.patient.phoneNumber || "").replace(/\D/g, "");
        const patientPlans = plansOfCareMap[p.patientId] ?? [];
        const haystack = [
          p.patient.firstName,
          p.patient.lastName,
          p.patient.preferredName,
          `${p.patient.firstName} ${p.patient.lastName}`,
          p.patient.phoneNumber,
          phoneDigits,
          insuranceMap.get(p.patient.insuranceId || "") || "",
          ...(() => {
            const effective = getEffectiveProviders(p, patientPlans);
            return [
              ...effective.providerIds.map((id) => providerMap.get(id) || ""),
              ...effective.groupIds.map((id) => providerGroupMap.get(id) || ""),
            ];
          })(),
          ...patientPlans.map((plan) => plan.caseStudyName || plan.name || ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      });
    }

    if (providerFilterSet.size > 0) {
      result = result.filter((p) => expandToProviderIds(getEffectiveProviders(p, plansOfCareMap[p.patientId])).some((id) => providerFilterSet.has(id)));
    }

    if (dateRange[0] && dateRange[1]) {
      const [start, end] = [dateRange[0], dateRange[1]].sort((a, b) => a!.toMillis() - b!.toMillis());
      const startDay = start!.startOf("day");
      const endDay = end!.startOf("day");
      result = result.filter((p) =>
        p.waitlist.availableDates.some((d) => {
          if (isDateRange(d)) {
            const rangeStart = DateTime.fromISO(d.startDate, { setZone: true }).startOf("day");
            const rangeEnd = DateTime.fromISO(d.endDate, { setZone: true }).startOf("day");
            return rangeStart <= endDay && rangeEnd >= startDay;
          }
          const dt = toDateTime(d).startOf("day");
          return dt >= startDay && dt <= endDay;
        }),
      );
    }

    const earliest = (p: OmniPatient) => Math.min(...filterPastAvailableDates(p.waitlist.availableDates).map((d) => toDateTime(d).toMillis()).filter((n) => !isNaN(n)));
    const sorted = [...result].sort((a, b) => earliest(a) - earliest(b));

    return sorted;
  }, [patients, searchQuery, selectedProviderIds, dateRange, providerMap, providerGroupMap, providerGroupMembersMap, insuranceMap, plansOfCareMap]);

  const eligiblePatientCount = useMemo(() => patients.filter((p) => p.waitlist.availableDates.length > 0).length, [patients]);

  const resetFilters = () => {
    posthog?.capture("[PENCILED] User reset waitlist filters", {
      orgName: organization?.name,
      organizationId: organization?.organizationId,
    });
    setSearchQuery("");
    setSelectedProviderIds([]);
    setDateRange([null, null]);
  };

  const handleProvidersChange = (value: string | number | (string | number)[]) => {
    const next = Array.isArray(value) ? (value as string[]) : [value as string];
    setSelectedProviderIds(next);
    posthog?.capture("[PENCILED] Filtering waitlist by providers", {
      orgName: organization?.name,
      organizationId: organization?.organizationId,
      providerIds: next,
    });
  };

  const handleDateRangeChange = (range: [DateTime | null, DateTime | null] | any) => {
    setDateRange(range);
    posthog?.capture("[PENCILED] Filtering waitlist by date range", {
      orgName: organization?.name,
      organizationId: organization?.organizationId,
      startDate: range?.[0]?.toISO?.(),
      endDate: range?.[1]?.toISO?.(),
    });
  };

  const exportPatientsToCSV = async () => {
    try {
      const eligible = filteredPatients.filter((p) => p.waitlist.availableDates.length > 0);
      if (eligible.length === 0) {
        return;
      }
      const localPlansMap: Record<string, ActivePlanOfCare[]> = { ...plansOfCareMap };
      if (userToken) {
        const missing = eligible.filter((p) => localPlansMap[p.patientId] === undefined);
        if (missing.length > 0) {
          const results = await Promise.allSettled(
            missing.map((p) =>
              queryClient.ensureQueryData({
                queryKey: patientPlansBriefQueryKey(p.patientId),
                queryFn: () => fetchPatientPlansBrief(p.patientId, userToken),
              }),
            ),
          );
          results.forEach((r, idx) => {
            localPlansMap[missing[idx].patientId] = r.status === "fulfilled" ? r.value : [];
          });
        }
      }
      const parser = new Parser();
      const csvRows: Record<string, any>[] = [];
      for (const patient of eligible) {
        const activePlans = (localPlansMap[patient.patientId] ?? []).filter((p) => !p.discharged);
        const planRows: (ActivePlanOfCare | null)[] = activePlans.length > 0 ? activePlans : [null];
        const effectiveProviders = getEffectiveProviders(patient, activePlans);
        const providerNames = [
          ...(effectiveProviders.providerIds.map((id) => providerMap.get(id)).filter(Boolean) as string[]),
          ...(effectiveProviders.groupIds.map((id) => providerGroupMap.get(id)).filter(Boolean) as string[]).map((name) => `${name} (Provider Group)`),
        ];
        for (const plan of planRows) {
          for (const providerName of providerNames) {
            for (const date of patient.waitlist.availableDates) {
              csvRows.push({
                "Patient ID": patient?.[EHR.webpt]?.webptPatientId,
                "First Name": patient.patient.firstName,
                "Last Name": patient.patient.lastName,
                Phone: patient.patient.phoneNumber,
                Insurance: insuranceMap.get(patient.patient.insuranceId || "") ?? "",
                "Case Study": plan ? plan.caseStudyName || plan.name || "" : "",
                "Visit Frequency": plan ? formatPlanFrequency(plan) ?? "" : "",
                "Provider Name": providerName,
                "Available Date": typeof date === "string" ? DateTime.fromISO(date).toFormat("yyyy-MM-dd") : isDateRange(date) ? `${DateTime.fromISO(date.startDate).toFormat("yyyy-MM-dd")} – ${DateTime.fromISO(date.endDate).toFormat("yyyy-MM-dd")}` : DateTime.fromISO(date.start).toFormat("yyyy-MM-dd"),
                "Group Name": patientGroupMap.get(patient.waitlist.patientGroupId || "") ?? "None",
              });
            }
          }
        }
      }
      const csv = parser.parse(csvRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `waitlist_export_${DateTime.now().toFormat("yyyy-MM-dd")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting patients to CSV", error);
    }
  };

  const activeFilterChips: { label: string; onRemove: () => void }[] = [];
  if (searchQuery.trim()) {
    activeFilterChips.push({ label: `Search: "${searchQuery.trim()}"`, onRemove: () => setSearchQuery("") });
  }
  selectedProviderIds.forEach((id) => {
    activeFilterChips.push({
      label: providerMap.get(id) || "Unknown provider",
      onRemove: () => setSelectedProviderIds((prev) => prev.filter((p) => p !== id)),
    });
  });
  if (dateRange[0] && dateRange[1]) {
    activeFilterChips.push({
      label: `${dateRange[0].toFormat("MMM d")} – ${dateRange[1].toFormat("MMM d")}`,
      onRemove: () => setDateRange([null, null]),
    });
  }

  const handleClose = () => {
    posthog?.capture("[PENCILED] User closed waitlist modal", {
      orgName: organization?.name,
      organizationId: organization?.organizationId,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "center",
          padding: { xs: 0, md: "24px" },
          outline: "none",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1180,
            height: { xs: "100%", md: "min(90vh, 880px)" },
            background: Colors.bg2,
            borderRadius: { xs: 0, md: surfaces.cardRadius },
            border: { xs: "none", md: `1px solid ${Colors.border}` },
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            color: surfaces.textPrimary,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              padding: { xs: "18px 20px 12px", md: "24px 28px 16px" },
              borderBottom: `1px solid ${Colors.border}`,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: surfaces.textPrimary, lineHeight: 1.1 }}>
                Waitlist
              </Typography>
              <Typography sx={{ color: Colors.grey1, fontSize: "13px", mt: 0.75 }}>View all the patients on the waitlist and their eligibility status.</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip title="Export to CSV" arrow>
                <IconButton
                  onClick={exportPatientsToCSV}
                  sx={{
                    color: surfaces.textPrimary,
                    background: surfaces.inactive,
                    borderRadius: surfaces.controlRadius,
                    border: `1px solid ${Colors.border}`,
                    transition: surfaces.transition,
                    "&:hover": { background: surfaces.inactiveHover },
                  }}
                >
                  <IosShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close" arrow>
                <IconButton
                  onClick={handleClose}
                  sx={{
                    color: surfaces.textPrimary,
                    background: surfaces.inactive,
                    borderRadius: surfaces.controlRadius,
                    border: `1px solid ${Colors.border}`,
                    transition: surfaces.transition,
                    "&:hover": { background: surfaces.inactiveHover },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filters */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
              alignItems: "center",
              padding: { xs: "14px 20px", md: "18px 28px" },
            }}
          >
            <Box
              sx={{
                position: "relative",
                flex: "1 1 260px",
                minWidth: 220,
              }}
            >
              <SearchIcon
                sx={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: Colors.grey1,
                  fontSize: "1.1rem",
                  pointerEvents: "none",
                }}
              />
              <Box
                component="input"
                type="text"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                placeholder="Search by patient, phone, provider…"
                sx={{
                  width: "100%",
                  height: 40,
                  padding: "0 14px 0 38px",
                  borderRadius: surfaces.controlRadius,
                  background: surfaces.inactive,
                  border: `1px solid ${Colors.border}`,
                  color: surfaces.textPrimary,
                  fontSize: "13px",
                  outline: "none",
                  transition: surfaces.transition,
                  "&::placeholder": { color: surfaces.textSubtle },
                  "&:hover": { background: surfaces.inactiveHover },
                  "&:focus": { borderColor: Colors.primary, background: surfaces.inactiveHover },
                }}
              />
            </Box>

            {providers.length > 0 && (
              <Box
                sx={{
                  flex: "1 1 200px",
                  minWidth: 180,
                  "& > div > div:first-of-type": {
                    backgroundColor: surfaces.inactive,
                    border: `1px solid ${Colors.border}`,
                    borderRadius: surfaces.controlRadius,
                    minHeight: 40,
                    padding: "0 12px",
                    boxSizing: "border-box",
                    color: surfaces.textPrimary,
                    fontSize: "13px",
                    transition: surfaces.transition,
                    "&:hover": { backgroundColor: surfaces.inactiveHover },
                  },
                }}
              >
                <Select value={selectedProviderIds} onChange={handleProvidersChange} options={providerOptions} multiple placeholder="All providers" shouldSearchOptions responsive />
              </Box>
            )}

            <Box
              sx={{
                flex: "1 1 220px",
                minWidth: 200,
                "& > div": {
                  backgroundColor: surfaces.inactive,
                  border: `1px solid ${Colors.border}`,
                  borderRadius: surfaces.controlRadius,
                  minHeight: 40,
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "13px",
                  transition: surfaces.transition,
                  "&:hover": { backgroundColor: surfaces.inactiveHover },
                },
                "& > div > div:first-of-type": {
                  flex: 1,
                  padding: "0 4px 0 12px",
                },
                "& > div > div:first-of-type > span": {
                  padding: 0,
                },
              }}
            >
              <DateRangePicker dateRange={dateRange} onChange={handleDateRangeChange} placeholder="Any date" responsive dropdownAlignment="right" />
            </Box>
          </Box>

          {/* Active filter chips */}
          {activeFilterChips.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.75,
                padding: { xs: "0 20px 12px", md: "0 28px 14px" },
              }}
            >
              {activeFilterChips.map((chip, idx) => (
                <Chip
                  key={`${chip.label}-${idx}`}
                  label={chip.label}
                  onDelete={chip.onRemove}
                  size="small"
                  sx={{
                    background: surfaces.inactive,
                    color: surfaces.textPrimary,
                    border: `1px solid ${Colors.border}`,
                    borderRadius: surfaces.controlRadius,
                    fontSize: "12px",
                    fontWeight: 500,
                    height: 26,
                    transition: surfaces.transition,
                    "&:hover": { background: surfaces.inactiveHover },
                    "& .MuiChip-deleteIcon": { color: Colors.grey1, fontSize: "14px", "&:hover": { color: surfaces.textPrimary } },
                  }}
                />
              ))}
              <Box
                component="button"
                onClick={resetFilters}
                sx={{
                  marginLeft: "4px",
                  background: "transparent",
                  border: "none",
                  color: Colors.grey1,
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "4px 6px",
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                  transition: surfaces.transition,
                  "&:hover": { color: Colors.error },
                }}
              >
                Clear all
              </Box>
            </Box>
          )}

          {/* Results count */}
          {!loadingPatients && eligiblePatientCount > 0 && (
            <Box
              sx={{
                padding: { xs: "0 20px 8px", md: "0 28px 10px" },
              }}
            >
              <Typography sx={{ color: Colors.grey1, fontSize: "12px", fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {filtersApplied && eligiblePatientCount !== filteredPatients.length
                  ? `Showing ${filteredPatients.length} of ${eligiblePatientCount} patients`
                  : `${filteredPatients.length} ${filteredPatients.length === 1 ? "patient" : "patients"}`}
              </Typography>
            </Box>
          )}

          {/* List */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: { xs: "0 20px 20px", md: "4px 28px 24px" },
            }}
          >
            {loadingPatients ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 280 }}>
                <LoadingWithMessage message="Loading waitlist…" size={28} customStyles={{ color: surfaces.textPrimary }} />
              </Box>
            ) : filteredPatients.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 280,
                  textAlign: "center",
                  gap: 1,
                  padding: "40px 20px",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: surfaces.cardRadius,
                    background: surfaces.inactive,
                    border: `1px solid ${Colors.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: Colors.grey1,
                  }}
                >
                  <InboxOutlinedIcon />
                </Box>
                <Typography sx={{ color: surfaces.textPrimary, fontWeight: 600, fontSize: "1rem" }}>
                  {filtersApplied ? "No patients match these filters" : "No patients on the waitlist yet"}
                </Typography>
                <Typography sx={{ color: Colors.grey1, fontSize: "13px", maxWidth: 360 }}>
                  {filtersApplied
                    ? "Try broadening the date range, removing a provider, or clearing your search."
                    : "Patients with available dates will appear here so you can fill cancellations quickly."}
                </Typography>
                {filtersApplied && (
                  <Box
                    component="button"
                    onClick={resetFilters}
                    sx={{
                      marginTop: 1,
                      padding: "8px 16px",
                      borderRadius: surfaces.controlRadius,
                      background: Colors.primary,
                      color: "#FFF",
                      border: `1px solid ${Colors.primary}`,
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: surfaces.transition,
                      "&:hover": { background: Colors.primaryDark, borderColor: Colors.primaryDark },
                    }}
                  >
                    Clear filters
                  </Box>
                )}
              </Box>
            ) : (
              <PaginatedList
                data={filteredPatients}
                pageSize={isSmallScreen ? 6 : 8}
                renderItem={(patient) => (
                  <PatientCard
                    key={patient.patientId}
                    patient={patient}
                    providerMap={providerMap}
                    providerGroupMap={providerGroupMap}
                    patientGroupName={patient.waitlist.patientGroupId ? patientGroupMap.get(patient.waitlist.patientGroupId) : undefined}
                    insuranceName={patient.patient.insuranceId ? insuranceMap.get(patient.patient.insuranceId) : undefined}
                    plansOfCare={plansOfCareMap[patient.patientId]}
                  />
                )}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ViewWaitlistModal;
